import {
  type InputHTMLAttributes,
  type ReactNode,
  forwardRef,
  useId,
  useState,
} from 'react';
import styles from './Input.module.css';

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  leftIcon?: ReactNode;
  /** When type="password", a toggle visibility button is rendered automatically. */
  showPasswordToggle?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      leftIcon,
      showPasswordToggle = false,
      type = 'text',
      id: idProp,
      className,
      ...rest
    },
    ref,
  ) => {
    const autoId = useId();
    const id = idProp ?? autoId;
    const errorId = `${id}-error`;
    const [visible, setVisible] = useState(false);

    const isPassword = type === 'password';
    const resolvedType = isPassword && visible ? 'text' : type;
    const hasRightAction = isPassword && showPasswordToggle;

    const inputClasses = [
      styles.input,
      leftIcon && styles.hasLeftIcon,
      hasRightAction && styles.hasRightAction,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        className={[
          styles.wrapper,
          error && styles.error,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {label && (
          <label htmlFor={id} className={styles.label}>
            {label}
          </label>
        )}

        <div className={styles.inputContainer}>
          {leftIcon && (
            <span className={styles.leftIcon} aria-hidden="true">
              {leftIcon}
            </span>
          )}

          <input
            ref={ref}
            id={id}
            type={resolvedType}
            className={inputClasses}
            aria-invalid={!!error || undefined}
            aria-describedby={error ? errorId : undefined}
            {...rest}
          />

          {hasRightAction && (
            <button
              type="button"
              className={styles.rightAction}
              onClick={() => setVisible((v) => !v)}
              aria-label={visible ? 'Ocultar contrasena' : 'Mostrar contrasena'}
              tabIndex={0}
            >
              {visible ? (
                /* Eye-off icon */
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                /* Eye icon */
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          )}
        </div>

        {error && (
          <div id={errorId} className={styles.errorMessage} role="alert">
            <svg
              className={styles.errorIcon}
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
